import React, { useState, useEffect } from 'react';
import axios from 'axios';
import printObituaryReview from '../utils/printObituaryReview';

const ObituaryForm = ({
  onGenerate,
  code,
  onSuccess,
  onError,
  loading: externalLoading,
  initialFormData,
  isRetry,
  existingObituaryId,
  /** async (formData) => result — if set, used instead of default /api/generate-obituary flow */
  customSubmit,
  /** Optional list of previously-used service location strings (FD mode) */
  serviceLocationOptions,
}) => {
  const [formData, setFormData] = useState({
    firstName: initialFormData?.firstName || '',
    lastName: initialFormData?.lastName || '',
    middleName: initialFormData?.middleName || '',
    nickname: initialFormData?.nickname || '',
    dateOfBirth: initialFormData?.dateOfBirth || '',
    dateOfDeath: initialFormData?.dateOfDeath || '',
    age: initialFormData?.age || '',
    homeCity: initialFormData?.homeCity || '',
    homeState: initialFormData?.homeState || '',
    birthplace: initialFormData?.birthplace || '',
    occupation: initialFormData?.occupation || '',
    education: initialFormData?.education || '',
    militaryService: initialFormData?.militaryService || '',
    hobbies: initialFormData?.hobbies || '',
    achievements: initialFormData?.achievements || '',
    survivedBy: initialFormData?.survivedBy || '',
    precededBy: initialFormData?.precededBy || '',
    serviceDate: initialFormData?.serviceDate || '',
    serviceLocation: initialFormData?.serviceLocation || '',
    favoriteQuote: initialFormData?.favoriteQuote || '',
    personalMessage: initialFormData?.personalMessage || ''
  });

  // Update form data when initialFormData changes
  useEffect(() => {
    if (initialFormData) {
      setFormData(prev => ({
        ...prev,
        ...initialFormData
      }));
    }
  }, [initialFormData]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Automatically calculate age when birth and death dates are entered
  useEffect(() => {
    if (formData.dateOfBirth && formData.dateOfDeath) {
      const birthDate = new Date(formData.dateOfBirth);
      const deathDate = new Date(formData.dateOfDeath);
      
      console.log('Calculating age:', {
        birthDate: formData.dateOfBirth,
        deathDate: formData.dateOfDeath,
        birthDateObj: birthDate,
        deathDateObj: deathDate
      });
      
      // Calculate age
      let age = deathDate.getFullYear() - birthDate.getFullYear();
      const monthDiff = deathDate.getMonth() - birthDate.getMonth();
      const dayDiff = deathDate.getDate() - birthDate.getDate();
      
      // Adjust age if death occurred before birthday in the final year
      if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        age--;
      }
      
      console.log('Calculated age:', age);
      
      // Only update if age is valid (positive number)
      if (age >= 0 && age < 150) {
        setFormData(prev => ({
          ...prev,
          age: age.toString()
        }));
      }
    }
  }, [formData.dateOfBirth, formData.dateOfDeath]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Generate obituary text from form data (for API submission)
  const generateObituaryText = () => {
    const parts = [];
    const fullName = [formData.firstName, formData.middleName, formData.lastName].filter(Boolean).join(' ');
    
    // Opening
    parts.push(`${fullName}${formData.age ? `, age ${formData.age},` : ''} passed away${formData.dateOfDeath ? ` on ${new Date(formData.dateOfDeath).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}` : ''}${formData.homeCity && formData.homeState ? ` in ${formData.homeCity}, ${formData.homeState}` : ''}.`);
    
    // Birth
    if (formData.dateOfBirth) {
      parts.push(`Born on ${new Date(formData.dateOfBirth).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}.`);
    }
    
    // Education
    if (formData.education) {
      parts.push(formData.education);
    }
    
    // Occupation/Career
    if (formData.occupation) {
      parts.push(formData.occupation);
    }
    
    // Hobbies/Interests
    if (formData.hobbies) {
      parts.push(formData.hobbies);
    }
    
    // Achievements
    if (formData.achievements) {
      parts.push(formData.achievements);
    }
    
    // Survived By
    if (formData.survivedBy) {
      parts.push(`${formData.firstName || 'They'} is survived by ${formData.survivedBy}.`);
    }
    
    // Preceded By
    if (formData.precededBy) {
      parts.push(`${formData.firstName || 'They'} was preceded in death by ${formData.precededBy}.`);
    }
    
    // Memorial Service
    if (formData.serviceDate || formData.serviceLocation) {
      let servicePart = 'A memorial service will be held';
      if (formData.serviceDate) {
        servicePart += ` on ${new Date(formData.serviceDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`;
      }
      if (formData.serviceLocation) {
        servicePart += ` at ${formData.serviceLocation}`;
      }
      parts.push(servicePart + '.');
    }
    
    // Personal Message
    if (formData.personalMessage) {
      parts.push(formData.personalMessage);
    }
    
    return parts.join('\n\n');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (typeof customSubmit === 'function') {
      setLoading(true);
      try {
        const result = await customSubmit(formData);
        if (onGenerate) {
          const fullName = [formData.firstName, formData.middleName, formData.lastName].filter(Boolean).join(' ');
          onGenerate({
            ...formData,
            obituaryText: result.obituary_text || result.obituaryText,
            fullName,
            credits_remaining: result.credits_remaining,
          });
        }
      } catch (err) {
        const errorMessage =
          err.response?.data?.error || err.message || 'Failed to generate obituary. Please try again.';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
      return;
    }

    // If code is provided, submit to API with AI generation (with fallback)
    if (code) {
      setLoading(true);
      try {
        const fullName = [formData.firstName, formData.middleName, formData.lastName].filter(Boolean).join(' ');
        let obituaryText;
        
        // Try AI generation first, fallback to simple generation if it fails
        try {
          const aiResponse = await axios.post('/api/generate-obituary', {
            formData: {
              ...formData,
              fullName
            }
          });
          obituaryText = aiResponse.data.obituary_text;
        } catch (aiError) {
          // If AI fails, use simple text generation as fallback
          console.log('AI generation failed, using fallback:', aiError.response?.data?.error || aiError.message);
          obituaryText = generateObituaryText();
        }
        
        const obituaryData = {
          deceased_name: fullName,
          birth_date: formData.dateOfBirth || null,
          death_date: formData.dateOfDeath || null,
          full_text: obituaryText,
          form_data: formData
        };

        // Then submit to generate endpoint to create PDF and save
        const response = await axios.post('/api/generate', {
          code: code.toUpperCase(),
          obituaryData,
          existingObituaryId: isRetry ? existingObituaryId : null
        });
        
        if (onSuccess) {
          onSuccess(response.data.obituary);
        }
      } catch (err) {
        const errorMessage = err.response?.data?.error || 'Failed to generate obituary. Please try again.';
        setError(errorMessage);
        if (onError) {
          onError(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    } else {
      // Use callback for non-API flow (try AI, fallback to simple)
      setLoading(true);
      try {
        const fullName = [formData.firstName, formData.middleName, formData.lastName].filter(Boolean).join(' ');
        let obituaryText;
        
        // Try AI generation first, fallback to simple generation if it fails
        try {
          const aiResponse = await axios.post('/api/generate-obituary', {
            formData: {
              ...formData,
              fullName
            }
          });
          obituaryText = aiResponse.data.obituary_text;
        } catch (aiError) {
          // If AI fails, use simple text generation as fallback
          console.log('AI generation failed, using fallback:', aiError.response?.data?.error || aiError.message);
          obituaryText = generateObituaryText();
        }
        
        if (onGenerate) {
          onGenerate({
            ...formData,
            obituaryText,
            fullName
          });
        }
      } catch (err) {
        const errorMessage = err.response?.data?.error || 'Failed to generate obituary. Please try again.';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="glass-card">
      <h2 className="mb-4">Obituary Information</h2>
      <form onSubmit={handleSubmit}>
        <div className="row mb-3">
          <div className="col-md-4">
            <label htmlFor="firstName" className="form-label">First Name *</label>
            <input
              type="text"
              className="form-control"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="Enter first name"
              required
            />
          </div>
          <div className="col-md-4">
            <label htmlFor="middleName" className="form-label">Middle Name</label>
            <input
              type="text"
              className="form-control"
              id="middleName"
              name="middleName"
              value={formData.middleName}
              onChange={handleChange}
              placeholder="Enter middle name"
            />
          </div>
          <div className="col-md-4">
            <label htmlFor="lastName" className="form-label">Last Name *</label>
            <input
              type="text"
              className="form-control"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Enter last name"
              required
            />
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-12">
            <label htmlFor="nickname" className="form-label">Nickname / Preferred Name</label>
            <input
              type="text"
              className="form-control"
              id="nickname"
              name="nickname"
              value={formData.nickname}
              onChange={handleChange}
              placeholder='e.g., "Jack"'
            />
            <small className="form-text" style={{ opacity: 0.7, fontSize: '0.75rem' }}>
              Optional — used in the obituary if provided
            </small>
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-md-4">
            <label htmlFor="dateOfBirth" className="form-label">Date of Birth</label>
            <input
              type="date"
              className="form-control"
              id="dateOfBirth"
              name="dateOfBirth"
              value={formData.dateOfBirth}
              onChange={handleChange}
            />
            <small className="form-text" style={{ opacity: 0.7, fontSize: '0.75rem' }}>
              Leave blank if unknown
            </small>
          </div>
          <div className="col-md-4">
            <label htmlFor="dateOfDeath" className="form-label">Date of Passing</label>
            <input
              type="date"
              className="form-control"
              id="dateOfDeath"
              name="dateOfDeath"
              value={formData.dateOfDeath}
              onChange={handleChange}
            />
            <small className="form-text" style={{ opacity: 0.7, fontSize: '0.75rem' }}>
              Leave blank if unknown
            </small>
          </div>
          <div className="col-md-4">
            {(() => {
              const bothDates = Boolean(formData.dateOfBirth && formData.dateOfDeath);
              return (
                <>
                  <label htmlFor="age" className="form-label">
                    Age {!bothDates && <span className="text-danger">*</span>}
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="149"
                    className="form-control"
                    id="age"
                    name="age"
                    value={formData.age}
                    onChange={handleChange}
                    placeholder={bothDates ? 'Auto-calculated' : 'Enter age manually'}
                    disabled={bothDates}
                  />
                  <small className="form-text" style={{ opacity: 0.7, fontSize: '0.75rem' }}>
                    {bothDates
                      ? 'Calculated automatically from dates'
                      : 'Required when either date is unknown'}
                  </small>
                </>
              );
            })()}
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-md-6">
            <label htmlFor="homeCity" className="form-label">Home City</label>
            <input
              type="text"
              className="form-control"
              id="homeCity"
              name="homeCity"
              value={formData.homeCity}
              onChange={handleChange}
              placeholder="Enter home city"
            />
          </div>
          <div className="col-md-6">
            <label htmlFor="homeState" className="form-label">Home State</label>
            <input
              type="text"
              className="form-control"
              id="homeState"
              name="homeState"
              value={formData.homeState}
              onChange={handleChange}
              placeholder="Enter home state"
            />
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-12">
            <label htmlFor="birthplace" className="form-label">Birthplace / Hometown</label>
            <input
              type="text"
              className="form-control"
              id="birthplace"
              name="birthplace"
              value={formData.birthplace}
              onChange={handleChange}
              placeholder='e.g., "Born in Cleveland, Ohio; raised on a farm in Medina County"'
            />
            <small className="form-text" style={{ opacity: 0.7, fontSize: '0.75rem' }}>
              Optional — where they were born or grew up, if different from home
            </small>
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-md-6">
            <label htmlFor="occupation" className="form-label">Occupation</label>
            <input
              type="text"
              className="form-control"
              id="occupation"
              name="occupation"
              value={formData.occupation}
              onChange={handleChange}
              placeholder="Enter occupation"
            />
          </div>
          <div className="col-md-6">
            <label htmlFor="education" className="form-label">Education</label>
            <input
              type="text"
              className="form-control"
              id="education"
              name="education"
              value={formData.education}
              onChange={handleChange}
              placeholder="Schools, degrees, etc."
            />
          </div>
        </div>

        <div className="mb-3">
          <label htmlFor="militaryService" className="form-label">Military Service</label>
          <input
            type="text"
            className="form-control"
            id="militaryService"
            name="militaryService"
            value={formData.militaryService}
            onChange={handleChange}
            placeholder="Branch, rank, years of service"
          />
        </div>

        <div className="mb-3">
          <label htmlFor="hobbies" className="form-label">Hobbies & Interests</label>
          <textarea
            className="form-control"
            id="hobbies"
            name="hobbies"
            rows="3"
            value={formData.hobbies}
            onChange={handleChange}
            placeholder="Describe hobbies, interests, passions"
          />
        </div>

        <div className="mb-3">
          <label htmlFor="achievements" className="form-label">Achievements & Accomplishments</label>
          <textarea
            className="form-control"
            id="achievements"
            name="achievements"
            rows="3"
            value={formData.achievements}
            onChange={handleChange}
            placeholder="Notable achievements, awards, contributions"
          />
        </div>

        <hr className="my-4" />
        <h3 className="mb-4">Family</h3>

        <div className="mb-3">
          <label htmlFor="survivedBy" className="form-label">Survived By</label>
          <textarea
            className="form-control"
            id="survivedBy"
            name="survivedBy"
            rows="3"
            value={formData.survivedBy}
            onChange={handleChange}
            placeholder="e.g., spouse John Doe; children Jane and James; grandchildren..."
          />
        </div>

        <div className="mb-3">
          <label htmlFor="precededBy" className="form-label">Preceded in Death By</label>
          <textarea
            className="form-control"
            id="precededBy"
            name="precededBy"
            rows="2"
            value={formData.precededBy}
            onChange={handleChange}
            placeholder="e.g., parents John and Mary Doe..."
          />
        </div>

        <hr className="my-4" />
        <h3 className="mb-4">Service Information</h3>

        <div className="row mb-3">
          <div className="col-md-6">
            <label htmlFor="serviceDate" className="form-label">Service Date</label>
            <input
              type="date"
              className="form-control"
              id="serviceDate"
              name="serviceDate"
              value={formData.serviceDate}
              onChange={handleChange}
            />
          </div>
          <div className="col-md-6">
            <label htmlFor="serviceLocation" className="form-label">Service Location</label>
            <input
              type="text"
              className="form-control"
              id="serviceLocation"
              name="serviceLocation"
              value={formData.serviceLocation}
              onChange={handleChange}
              placeholder='e.g., "First Baptist Church" (address optional)'
              list={Array.isArray(serviceLocationOptions) && serviceLocationOptions.length ? 'fd-service-location-options' : undefined}
              autoComplete="off"
            />
            {Array.isArray(serviceLocationOptions) && serviceLocationOptions.length > 0 && (
              <>
                <datalist id="fd-service-location-options">
                  {serviceLocationOptions.map((opt) => (
                    <option key={opt} value={opt} />
                  ))}
                </datalist>
                <small className="form-text" style={{ opacity: 0.7, fontSize: '0.75rem' }}>
                  Start typing to pick a saved location, or enter a new one.
                  Manage the list under <strong>Service Locations</strong> in the sidebar.
                </small>
              </>
            )}
          </div>
        </div>

        <div className="mb-3">
          <label htmlFor="favoriteQuote" className="form-label">Favorite Quote or Saying</label>
          <input
            type="text"
            className="form-control"
            id="favoriteQuote"
            name="favoriteQuote"
            value={formData.favoriteQuote}
            onChange={handleChange}
            placeholder="A quote or saying they were known for"
          />
        </div>

        <div className="mb-4">
          <label htmlFor="personalMessage" className="form-label">Personal Message</label>
          <textarea
            className="form-control"
            id="personalMessage"
            name="personalMessage"
            rows="4"
            value={formData.personalMessage}
            onChange={handleChange}
            placeholder="Additional personal message or memories"
          />
        </div>

        {error && (
          <div className="alert alert-danger mb-3" role="alert">
            {error}
          </div>
        )}

        <div className="fd-form-actions">
          <button
            type="button"
            className="btn btn-lg btn-print-review"
            onClick={() => printObituaryReview(formData)}
            disabled={loading || externalLoading}
            title="Print a review sheet so the family can confirm every detail before you generate the obituary"
          >
            <svg
              className="btn-print-review-icon"
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M6 9V3h12v6" />
              <rect x="3" y="9" width="18" height="9" rx="2" />
              <rect x="6" y="14" width="12" height="7" rx="1" />
              <circle cx="17" cy="12" r="0.6" fill="currentColor" />
            </svg>
            Print What We Collected for Review
          </button>
          <button
            type="submit"
            className="btn btn-primary btn-lg"
            disabled={loading || externalLoading}
          >
            {loading || externalLoading ? 'Generating...' : (code ? 'Generate Obituary & PDF' : 'Generate Obituary')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ObituaryForm;




